import React, { useEffect, useRef } from 'react';
import { Separator, Toolbar, createButton, createDropdown } from 'react-simple-wysiwyg';
import {
    RiAlignLeft, RiItalic, RiUnderline, RiStrikethrough,
    RiAlignRight, RiAlignCenter, RiAlignJustify, RiLink,
    RiSubscript, RiSuperscript
} from 'react-icons/ri';
import { RiImage2Fill } from 'react-icons/ri';
import { GoHorizontalRule } from 'react-icons/go';
import { LuUndo2, LuRedo2 } from 'react-icons/lu';
import { MdFormatBold, MdFormatClear } from 'react-icons/md';
import { AiOutlineOrderedList, AiOutlineUnorderedList } from 'react-icons/ai';
import { IoDocumentAttach } from 'react-icons/io5';
import "./WysiwygEditorToolbar.css"

const WysiwygEditorToolbar = ({ onAddAttachment }) => {
    const fileInputRef = useRef(null);
    const attachFileInputRef = useRef(null);

    useEffect(() => {
        const toolbarElement = document.querySelector('.rsw-toolbar');
        if (toolbarElement) toolbarElement.classList.add('hide-scrollbar');
    }, []);

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                document.execCommand('insertImage', false, event.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAttachmentUpload = (e) => {
        const files = Array.from(e.target.files || []);
        if (onAddAttachment) {
            onAddAttachment(files);
        }
    };

    const BtnImage = createButton(
        'Image',
        <span className="editor-toolbar-button">
            <RiImage2Fill className="editor-toolbar-icon" />
        </span>,
        ({ $selection }) => {
            if ($selection && fileInputRef.current) {
                fileInputRef.current.click();
            }
        }
    );

    const BtnAddAttachments = createButton(
        'Attach Files',
        <span className="editor-toolbar-button">
            <IoDocumentAttach className="editor-toolbar-icon" />
        </span>,
        ({ $selection }) => {
            if ($selection && attachFileInputRef.current) {
                attachFileInputRef.current.click();
            }
        }
    );

    const TypographyType = createDropdown('Text Styles', [
        ['Normal', 'formatBlock', 'DIV'],
        ['Header 1', 'formatBlock', 'H1'],
        ['Header 2', 'formatBlock', 'H2'],
        ['Header 3', 'formatBlock', 'H3'],
        ['Header 4', 'formatBlock', 'H4'],
        ['Header 5', 'formatBlock', 'H5'],
        ['Header 6', 'formatBlock', 'H6']
    ]);

    return (
        <>
            {/* Hidden file inputs */}
            <input 
                ref={fileInputRef}
                type="file" 
                accept="image/*" 
                style={{ display: 'none' }} 
                onChange={handleImageUpload} 
            />
            <input 
                ref={attachFileInputRef}
                type="file" 
                multiple 
                style={{ display: 'none' }} 
                onChange={handleAttachmentUpload} 
            />
            
            <Toolbar>
                <BtnUndo />
                <BtnRedo />
                <Separator />
                <TypographyType />
                <BtnBold />
                <BtnItalics />
                <BtnUnderline />
                <BtnStrikeThrough />
                <Separator />
                <BtnAlignLeft />
                <BtnAlignCenter />
                <BtnAlignRight />
                <BtnAlignJustify />
                <Separator />
                <BtnOrderedList />
                <BtnUnorderedList />
                <BtnHorizontalLine />
                <Separator />
                <BtnSubscript />
                <BtnSuperscript />
                <Separator />
                <BtnLink />
                <BtnImage />
                {onAddAttachment && <BtnAddAttachments />}
                <BtnClearFormatting />
            </Toolbar>
        </>
    );
};

// Rest of your button definitions remain the same...
const IconButton = (label, Icon, command) =>
    createButton(
        label,
        <span className="editor-toolbar-button">
            <Icon className="editor-toolbar-icon" />
        </span>,
        command
    );

const BtnClearFormatting = IconButton('Clear Formatting', MdFormatClear, 'removeFormat');
const BtnHorizontalLine = IconButton('Horizontal Line', GoHorizontalRule, 'insertHorizontalRule');
const BtnSubscript = IconButton('Subscript', RiSubscript, 'subscript');
const BtnSuperscript = IconButton('Superscript', RiSuperscript, 'superscript');
const BtnUnorderedList = IconButton('Unordered List', AiOutlineUnorderedList, 'insertUnorderedList');
const BtnOrderedList = IconButton('Ordered List', AiOutlineOrderedList, 'insertOrderedList');
const BtnBold = IconButton('Bold', MdFormatBold, 'bold');
const BtnItalics = IconButton('Italics', RiItalic, 'italic');
const BtnUnderline = IconButton('Underline', RiUnderline, 'underline');
const BtnStrikeThrough = IconButton('StrikeThrough', RiStrikethrough, 'strikeThrough');
const BtnUndo = IconButton('Undo', LuUndo2, 'undo');
const BtnRedo = IconButton('Redo', LuRedo2, 'redo');
const BtnAlignCenter = IconButton('Align Center', RiAlignCenter, 'justifyCenter');
const BtnAlignLeft = IconButton('Align Left', RiAlignLeft, 'justifyLeft');
const BtnAlignRight = IconButton('Align Right', RiAlignRight, 'justifyRight');
const BtnAlignJustify = IconButton('Justify', RiAlignJustify, 'justifyFull');

const BtnLink = createButton(
    'Link',
    <span className="editor-toolbar-button">
        <RiLink className="editor-toolbar-icon" />
    </span>,
    ({ $selection }) => {
        if ($selection?.nodeName === 'A') {
            document.execCommand('unlink');
        } else {
            document.execCommand('createLink', false, prompt('URL', '') || undefined);
        }
    }
);

export default WysiwygEditorToolbar;